import { yupResolver } from '@hookform/resolvers/yup'
import { getMessage } from '@limitless-minds/app-messages'
import { Button } from '@limitless-minds/ui-button'
import { Input } from '@limitless-minds/ui-input'
import { Stack } from '@mui/material'
import React, { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { base as baseConfig } from '@/config'
import { useLogin } from '@/data/auth'
import { useStore } from '@/store'
import { Error as IError } from '@/types/Error'
import ErrorMessage from '@/ui/atoms/ErrorMessage'

import { FIELDS, schema } from './schema'

type FormValues = {
  email: string
  password: string
}

interface LoginFormProps {
  setShowMfaForm: React.Dispatch<React.SetStateAction<number>>
  setCodeResetTimeOut: React.Dispatch<React.SetStateAction<number>>
}
const LoginForm: React.FC<LoginFormProps> = ({ setShowMfaForm, setCodeResetTimeOut }) => {
  const [failedLoginMessage, setFailedLoginMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { setEmail } = useStore()
  const { mutate: login } = useLogin()

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      [FIELDS.EMAIL]: '',
      [FIELDS.PASSWORD]: '',
    },
  })

  const onSubmit: SubmitHandler<FormValues> = (data: FormValues) => {
    const { email, password } = data
    setIsSubmitting(true)
    setEmail(email)
    login(
      { body: { email, password } },
      {
        onSuccess: (response: any) => {
          setFailedLoginMessage('')
          setIsSubmitting(false)
          // Clear the local storage of Zustand store
          useStore.persist.clearStorage()
          if (response.data.requireMfa) {
            setShowMfaForm((prev) => prev + 1)
            setCodeResetTimeOut(response.data.resendCooldownMinutes * 60)
          } else {
            const url = sessionStorage.getItem('redirectUrl')
            sessionStorage.removeItem('redirectUrl')
            if (url) {
              window.location.href = `${url}`
            } else {
              window.location.href = `${baseConfig.webAppBaseUrl}`
            }
          }
        },
        onError: (error) => {
          setIsSubmitting(false)
          const errorResponse = error as IError
          if (errorResponse.response?.data?.error?.errorCode === 'AUTH001') {
            setShowMfaForm(0)
          }
          const errorMessage = getMessage(
            errorResponse.response?.data?.error?.errorCode,
            errorResponse.response?.data?.error?.params
          )
          setFailedLoginMessage(errorMessage)
        },
      }
    )
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ display: 'flex', flexDirection: 'column', gap: '2rem', width: '100%' }}
    >
      <Stack gap='0.75rem'>
        {failedLoginMessage !== '' && <ErrorMessage>{failedLoginMessage}</ErrorMessage>}
        <Controller
          name={FIELDS.EMAIL}
          control={control}
          render={({ field }) => (
            <Input
              label={'Email'}
              {...field}
              error={!!errors?.[FIELDS.EMAIL]}
              errorMessage={errors?.[FIELDS.EMAIL]?.message}
            />
          )}
        />
        <Controller
          name={FIELDS.PASSWORD}
          control={control}
          render={({ field }) => (
            <Input
              label={'Password'}
              {...field}
              password
              error={!!errors?.[FIELDS.PASSWORD]}
              errorMessage={errors?.[FIELDS.PASSWORD]?.message}
            />
          )}
        />
      </Stack>
      <Button type='submit' width='100%' disabled={isSubmitting}>
        Login
      </Button>
    </form>
  )
}

export default LoginForm
